"use client";

import { useEffect } from "react";
import Image from "next/image";
import RotateCcw from "lucide-react/dist/esm/icons/rotate-ccw";
import Home from "lucide-react/dist/esm/icons/house";
import ServerCrash from "lucide-react/dist/esm/icons/server-crash";
import { DiscordIcon, DISCORD_TRACKED_INVITE_URL } from "@/components/ui/discord-cta";
import { reportErrorToDiscord } from "@/lib/error-reporting";
import { getPosthog } from "@/lib/analytics/posthog";

/**
 * Next.js root global error boundary — catches errors thrown inside the root
 * layout (e.g. ThemeProvider, AuthProvider, font loading) where the regular
 * ErrorBoundary component tree is not yet mounted.
 *
 * This component must render its own <html> and <body> because the root layout
 * is unavailable when this fires.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log to console in all environments; production reporting via the RPC
    // isn't available here because Supabase client may not be initialised.
    console.error("[GlobalError]", error);

    try {
      getPosthog().captureException(error, { digest: error.digest });
    } catch {
      // captureException must never re-throw out of the error boundary
    }

    void reportErrorToDiscord({
      source: "GlobalError",
      userMessage: "A critical error prevented the application from loading.",
      error,
      severity: "fatal",
      metadata: { digest: error.digest },
    });

  }, [error]);

  return (
    <html lang="en">
      <body style={{ margin: 0, background: "#0a0a0a", color: "#fafafa", fontFamily: "system-ui, sans-serif" }}>
        <div
          style={{
            minHeight: "100dvh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            gap: "1.5rem",
          }}
        >
          {/* global-error replaces the root layout after it crashed: a full page
              load is the reset we want, not a client-side transition. */}
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
          <a
            href="/"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.65rem",
              textDecoration: "none",
              color: "rgba(255,255,255,0.85)",
            }}
          >
            <Image
              src="/logo.webp"
              alt="UEFNMarketplace"
              width={36}
              height={36}
              loading="eager"
              decoding="async"
              style={{ borderRadius: 8 }}
            />
            <span style={{ fontFamily: "system-ui, sans-serif", fontWeight: 700, fontSize: 15, letterSpacing: "-0.02em" }}>
              UEFN<span style={{ color: "#22d3ee" }}>Marketplace</span>
            </span>
          </a>
          <div
            style={{
              maxWidth: 420,
              width: "100%",
              background: "#111",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 16,
              padding: "2.5rem",
              textAlign: "center",
              boxShadow: "0 25px 50px rgba(0,0,0,0.6)",
            }}
          >
            {/* Icon */}
            <div
              style={{
                display: "inline-flex",
                padding: "1.25rem",
                borderRadius: 16,
                background: "rgba(239,68,68,0.1)",
                border: "1px solid rgba(239,68,68,0.2)",
                marginBottom: "1.5rem",
              }}
            >
              <ServerCrash style={{ width: 40, height: 40, color: "rgb(239,68,68)" }} />
            </div>

            {/* Badge */}
            <div style={{ marginBottom: "1rem" }}>
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: "rgba(239,68,68,0.7)",
                  background: "rgba(239,68,68,0.05)",
                  border: "1px solid rgba(239,68,68,0.15)",
                  padding: "3px 12px",
                  borderRadius: 99,
                }}
              >
                {error.digest ? `CRITICAL ERROR · ${error.digest}` : "CRITICAL ERROR"}
              </span>
            </div>

            <h1
              style={{
                fontSize: "1.5rem",
                fontWeight: 700,
                marginBottom: "0.75rem",
                fontFamily: "'Chakra Petch', Outfit, system-ui, sans-serif",
              }}
            >
              Application Failed to Load
            </h1>

            <p style={{ color: "rgba(255,255,255,0.5)", lineHeight: 1.6, marginBottom: "0.75rem" }}>
              A critical error prevented the page from rendering. Your data is safe.
            </p>

            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", marginBottom: "1.5rem" }}>
              This issue has been logged automatically.
            </p>

            {process.env.NODE_ENV === "development" && (
              <div
                style={{
                  background: "rgba(239,68,68,0.05)",
                  border: "1px solid rgba(239,68,68,0.15)",
                  borderRadius: 8,
                  padding: "0.75rem",
                  marginBottom: "1.5rem",
                  textAlign: "left",
                  fontFamily: "monospace",
                  fontSize: 12,
                  color: "rgb(239,68,68)",
                  wordBreak: "break-all",
                  maxHeight: 120,
                  overflow: "auto",
                }}
              >
                {error.message}
              </div>
            )}

            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={reset}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.5rem 1.25rem",
                  borderRadius: 8,
                  background: "white",
                  color: "#0a0a0a",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                  border: "none",
                }}
              >
                <RotateCcw style={{ width: 16, height: 16 }} />
                Try Again
              </button>
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- full reload resets the crashed root layout */}
              <a
                href="/"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.5rem 1.25rem",
                  borderRadius: 8,
                  background: "transparent",
                  color: "rgba(255,255,255,0.7)",
                  fontWeight: 500,
                  fontSize: 14,
                  cursor: "pointer",
                  border: "1px solid rgba(255,255,255,0.15)",
                  textDecoration: "none",
                }}
              >
                <Home style={{ width: 16, height: 16 }} />
                Go Home
              </a>
              <a
                href={DISCORD_TRACKED_INVITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.5rem 1.25rem",
                  borderRadius: 8,
                  background: "transparent",
                  color: "rgba(255,255,255,0.7)",
                  fontWeight: 500,
                  fontSize: 14,
                  cursor: "pointer",
                  border: "1px solid rgba(255,255,255,0.15)",
                  textDecoration: "none",
                }}
              >
                <DiscordIcon style={{ width: 16, height: 16 }} />
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
